import React from "react";
import { Link } from "react-router-dom";
import { Shield, CheckCircle, XCircle, Mail, MapPin, Building, ArrowLeft } from "lucide-react";

export default function TrustPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-950/90 backdrop-blur sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-semibold tracking-wide">
            Social Dispute Co
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
          </nav>
          <Link
            to="/contact"
            className="rounded-2xl bg-white text-slate-950 px-4 py-2 text-sm font-medium shadow-lg hover:opacity-90 transition"
          >
            Start Case Review
          </Link>
        </div>
      </header>

      <main className="py-12">
        <div className="mx-auto max-w-4xl px-6">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          {/* Page Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold">Business Verification</h1>
              <p className="text-slate-400 mt-1">Transparency and trust information</p>
            </div>
          </div>

          {/* Legal Entity Information */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-400" />
              Legal Entity Information
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-slate-500 text-sm">Legal Business Name</p>
                  <p className="text-lg font-medium">8eight8 LLC</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Trade Name (DBA)</p>
                  <p className="text-lg font-medium">Social Dispute Co</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Business Type</p>
                  <p className="text-lg font-medium">Limited Liability Company</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-slate-500 text-sm">Jurisdiction</p>
                  <p className="text-lg font-medium">United States</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Location</p>
                  <p className="text-lg font-medium">Honolulu, Hawaii</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Founded</p>
                  <p className="text-lg font-medium">2024</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Contact Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-slate-500 text-sm">Email</p>
                  <a href="mailto:intake@socialdisputeco.com" className="text-blue-400 hover:text-blue-300">
                    intake@socialdisputeco.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-slate-500 text-sm">Location</p>
                  <p className="text-white">Honolulu, Hawaii, United States</p>
                </div>
              </div>
            </div>
          </section>

          {/* Service Transparency */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6">Service Transparency</h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              Social Dispute Co provides structured documentation and guidance for dispute 
              submission through official platform channels. We help individuals and businesses 
              organize their case materials, timelines, and supporting evidence for platform 
              appeal processes.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-blue-400 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> What We Provide
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                    <span>Structured case documentation</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                    <span>Timeline organization</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                    <span>Policy-aligned submission guidance</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                    <span>Transparent process and pricing</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <XCircle className="w-5 h-5" /> What We Do NOT Do
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-slate-300">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                    <span>No password access</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                    <span>No account logins</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                    <span>No unauthorized methods or "hacks"</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-300">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                    <span>No outcome guarantees</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Independence Statement */}
          <section className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-4 text-yellow-100">Independence Statement</h2>
            <p className="text-yellow-100/80 leading-relaxed">
              Social Dispute Co is an <strong>independent service</strong> and is 
              <strong> not affiliated with Meta, Instagram, Facebook, TikTok, YouTube, X (Twitter), 
              or any other social media platform</strong>. We do not have special access to 
              platform systems and cannot guarantee any specific outcome. All appeals must be 
              submitted by the account holder through official platform channels.
            </p>
          </section>

          {/* Founder Section */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6">About the Founder</h2>
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center text-2xl font-bold text-blue-400 flex-shrink-0">
                MB
              </div>
              <div>
                <h3 className="text-lg font-semibold">Mark Becker</h3>
                <p className="text-slate-400 text-sm mb-3">Founder, Social Dispute Co</p>
                <p className="text-slate-300 leading-relaxed">
                  Social Dispute Co was founded to provide a transparent, structured alternative 
                  to unverified "recovery services." After experiencing account issues firsthand 
                  and researching the landscape of available help, Mark built this service to 
                  focus on what actually matters: organized documentation, clear processes, and 
                  honest communication about what can and cannot be done.
                </p>
              </div>
            </div>
          </section>

          {/* Trust Score Response Section (ScamAdviser) */}
          <section id="scamadviser" className="rounded-2xl border border-yellow-500/30 bg-yellow-500/5 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              About ScamAdviser &amp; Other Trust-Score Websites
            </h2>
            <div className="space-y-4 text-slate-300">
              <p>
                Automated trust-scoring sites such as <strong className="text-white">ScamAdviser</strong>, Scamadviser.com,
                and similar services may display a lower score for newly registered businesses like ours.
                These scores are generated by algorithms that weigh domain age heavily — they are
                <em> not</em> based on actual customer experiences or verified complaints.
                Here is the full transparency on why our ScamAdviser score may appear low and what we are doing about it:
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">We are a new business (2024)</p>
                    <p className="text-sm text-slate-400">Domain age is a factor in trust scores. Our business is legitimate but recently established.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Registered U.S. LLC</p>
                    <p className="text-sm text-slate-400">8eight8 LLC is a registered business entity in Hawaii. Business registration is verifiable through the Hawaii DCCA.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Secure Payment Processing</p>
                    <p className="text-sm text-slate-400">We use Stripe — PCI-compliant payments with built-in buyer protection and chargeback/dispute resolution.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Transparent About Limitations</p>
                    <p className="text-sm text-slate-400">We clearly state we cannot guarantee outcomes. Honest disclosure is a trust signal.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Real Contact Information</p>
                    <p className="text-sm text-slate-400">Phone: 808-301-2134 | Email: intake@socialdisputeco.com | Location: Honolulu, Hawaii</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-xl bg-slate-800/50">
                <p className="text-sm text-slate-400">
                  <strong className="text-white">Note:</strong> Trust scores from automated websites are based on algorithms, not actual customer experiences. 
                  We encourage you to verify our business registration, read our terms, and contact us directly with any concerns before purchasing.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center py-8">
            <h2 className="text-2xl font-semibold mb-4">Have Questions?</h2>
            <p className="text-slate-400 mb-6">
              Review our FAQ or start your case intake to learn more about how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/faq"
                className="rounded-2xl border border-white/20 px-6 py-3 text-white font-medium hover:bg-white/5 transition"
              >
                View FAQ
              </Link>
              <Link
                to="/contact"
                className="rounded-2xl bg-white px-6 py-3 text-slate-950 font-medium hover:opacity-90 transition"
              >
                Start Case Intake
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <p className="text-slate-400 text-sm">
            Social Dispute Co is a registered trade name of 8eight8 LLC
          </p>
          <p className="text-slate-500 text-sm mt-1">
            Honolulu, Hawaii | United States | intake@socialdisputeco.com
          </p>
          <p className="text-slate-500 text-sm mt-4">
            © 2026 8eight8 LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
